import React from 'react';
import styled, { css } from 'styled-components';

import {
  baseTravel,
  SVG_PAD,
  useButtonMetrics,
} from 'src/dispensary/core-menu/components/header/concierge-nav-button/concierge-button-effects';

const RING_LENGTH = 34;

export function ConciergeRing({
  targetRef,
  testId,
}: {
  targetRef: React.RefObject<HTMLElement>;
  testId: string;
}): JSX.Element | null {
  const metrics = useButtonMetrics(targetRef);
  if (!metrics) return null;

  const rectProps = {
    x: SVG_PAD,
    y: SVG_PAD,
    width: metrics.w,
    height: metrics.h,
    rx: Math.min(metrics.radius, metrics.h / 2),
    pathLength: 100,
  };

  return (
    <RingSvg
      aria-hidden='true'
      data-testid={testId}
      viewBox={`0 0 ${metrics.w + SVG_PAD * 2} ${metrics.h + SVG_PAD * 2}`}
    >
      <BaseRing {...rectProps} />
      <TravelRing {...rectProps} data-testid={`${testId}-travel`} />
    </RingSvg>
  );
}

const RingSvg = styled.svg`
  height: calc(100% + ${SVG_PAD * 2}px);
  left: -${SVG_PAD}px;
  overflow: visible;
  pointer-events: none;
  position: absolute;
  top: -${SVG_PAD}px;
  width: calc(100% + ${SVG_PAD * 2}px);
`;

const BaseRing = styled.rect`
  fill: none;
  stroke: var(--dcm-concierge-accent, #d4a843);
  stroke-opacity: 0.25;
  stroke-width: 1;
`;

const TravelRing = styled.rect`
  fill: none;
  filter: drop-shadow(0 0 4px var(--dcm-concierge-accent, #d4a843));
  stroke: var(--dcm-concierge-accent, #d4a843);
  stroke-dasharray: ${RING_LENGTH} ${100 - RING_LENGTH};
  stroke-linecap: round;
  stroke-width: 2.5;

  @media (prefers-reduced-motion: no-preference) {
    animation: ${baseTravel} 3s linear infinite;
  }
`;

export const conciergeSurfaceCss = css`
  background: radial-gradient(
    120% 140% at 15% 0%,
    color-mix(in oklch, var(--dcm-color-surface-hover, #182f28) 75%, var(--dcm-concierge-accent, #d4a843)) 0%,
    var(--dcm-color-surface, #0e201a) 55%,
    color-mix(in oklch, var(--dcm-color-surface, #0e201a) 70%, black) 100%
  );
  color: var(--dcm-color-text-primary, #f8efd9);
`;

export const conciergeBadgeCss = css`
  align-items: center;
  background: linear-gradient(
    150deg,
    var(--dcm-concierge-accent-soft, rgba(212, 168, 67, 0.3)),
    var(--dcm-concierge-accent-glow, rgba(212, 168, 67, 0.15))
  );
  color: var(--dcm-concierge-accent, #d4a843);
  display: flex;
  height: 40px;
  justify-content: center;
  position: relative;
  width: 40px;
`;

export const conciergeLabelCss = css`
  color: var(--dcm-concierge-accent, #d4a843);
  font-size: var(--dcm-font-size-xs, 12px);
  font-weight: var(--dcm-font-weight-bold, 700);
  letter-spacing: 0.08em;
  margin: 0;
`;

export const conciergePillCss = css`
  background: linear-gradient(135deg, var(--dcm-concierge-accent, #d4a843), var(--dcm-concierge-accent-deep, #b88e35));
  border: none;
  border-radius: var(--dcm-radius-buttons, 4px);
  color: var(--dcm-color-text-inverted, #0e201a);
  cursor: pointer;
  font-size: var(--dcm-font-size-sm, 13px);
  font-weight: var(--dcm-font-weight-bold, 700);
  margin-top: 4px;
  padding: 9px 18px;
  transition: filter 0.2s;

  @media (hover: hover) {
    &:hover {
      filter: brightness(1.06);
    }
  }

  &:focus-visible {
    outline: 2px solid var(--dcm-concierge-accent, #d4a843);
    outline-offset: 3px;
  }
`;
